/**
 * PlacementPipeline - the Kanban view of a customer's Placements (§7.3). One column per
 * PLACEMENT_PIPELINE_SPALTEN entry (the funnel; abgebrochen placements fall off-pipeline and are
 * not shown here). Each card links to the full-screen Ausschreibung detail.
 *
 * `cardMeta` (optional) renders one extra line per card — the firm-wide AusschreibungenListScreen
 * uses it for the customer name; the per-customer tab passes nothing.
 */

import type { ReactNode } from 'react';
import { EntityLink } from '../../components/ui/EntityLink';
import { PLACEMENT_PIPELINE_SPALTEN } from '@brokly/shared';
import type { PlacementListItem } from '@brokly/shared';

export function PlacementPipeline<T extends PlacementListItem>({
  items,
  onOpen,
  cardMeta,
}: {
  items: T[];
  onOpen: (id: string) => void;
  cardMeta?: (item: T) => ReactNode;
}) {
  return (
    <div className="flex gap-3 overflow-x-auto pb-2" data-testid="placement-pipeline">
      {PLACEMENT_PIPELINE_SPALTEN.map((spalte) => {
        const cards = items.filter((i) => (spalte.statuses as readonly string[]).includes(i.status));
        return (
          <div key={spalte.key} className="flex w-56 shrink-0 flex-col gap-2">
            <div className="flex items-center justify-between px-1 text-xs font-medium text-muted-foreground">
              <span>{spalte.label}</span>
              <span className="tabular-nums">{cards.length}</span>
            </div>
            <div className="flex flex-col gap-2">
              {cards.map((c) => (
                <div key={c.id} className="rounded-lg border bg-card p-3 text-sm">
                  <EntityLink onActivate={() => onOpen(c.id)} className="mono-num text-xs font-medium">
                    {c.placement_number}
                  </EntityLink>
                  {cardMeta && <p className="mt-1 truncate text-xs text-foreground">{cardMeta(c)}</p>}
                  <p className="mt-1 truncate text-xs text-muted-foreground">{c.branchen_label}</p>
                </div>
              ))}
              {cards.length === 0 && (
                <div className="rounded-lg border border-dashed p-3 text-center text-xs text-muted-foreground">
                  -
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
